import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Tabs } from 'antd';
import { UserOutlined, LockOutlined, MobileOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { responsibleAuthService, ResponsibleLoginParams } from '../services/responsibleAuthService';

const { TabPane } = Tabs;

const ResponsibleLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [passwordForm] = Form.useForm();
  const [smsForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [activeTab, setActiveTab] = useState('sms'); // 默认显示短信验证码登录

  // 倒计时效果
  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 发送验证码
  const handleSendCode = async () => {
    try {
      const phone = smsForm.getFieldValue('phone');
      if (!phone) {
        message.error('请先输入手机号');
        return;
      }

      setSendingCode(true);
      await responsibleAuthService.sendVerificationCode({ phone });
      message.success('验证码发送成功');
      setCountdown(60); // 60秒倒计时
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('验证码发送失败，请稍后重试');
      }
    } finally {
      setSendingCode(false);
    }
  };

  // 密码登录
  const handlePasswordLogin = async (values: ResponsibleLoginParams) => {
    try {
      setLoading(true);
      const { token, user } = await responsibleAuthService.login(values);
      message.success('登录成功');
      navigate('/responsible/project', { replace: true });
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.status === 401) {
        message.error('用户名或密码错误');
      } else if (error.response?.status === 403) {
        message.error('账号已被禁用');
      } else {
        message.error('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  // 验证码登录
  const handleSmsLogin = async (values: ResponsibleLoginParams) => {
    try {
      setLoading(true);
      const { token, user } = await responsibleAuthService.login(values);
      message.success('登录成功');
      navigate('/responsible/project', { replace: true });
    } catch (error: any) {
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.status === 400) {
        message.error('验证码错误或已过期');
      } else {
        message.error('登录失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      background: '#f0f2f5'
    }}>
      <Card style={{ width: 420, boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
        <h2 style={{ textAlign: 'center', marginBottom: 24 }}>负责人登录</h2>
        
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          centered
        >
          <TabPane tab={
            <span>
              <MobileOutlined />
              短信验证码
            </span>
          } key="sms">
            <Form
              form={smsForm}
              onFinish={handleSmsLogin}
              layout="vertical"
            >
              <Form.Item
                name="phone"
                rules={[
                  { required: true, message: '请输入手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确' }
                ]}
              >
                <Input 
                  prefix={<MobileOutlined />} 
                  placeholder="手机号" 
                  size="large"
                />
              </Form.Item>
              
              <Form.Item
                name="code"
                rules={[
                  { required: true, message: '请输入验证码' },
                  { 
                    pattern: /^\d{6}$/, 
                    message: '请输入6位数字验证码' 
                  }
                ]}
              >
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    prefix={<SafetyOutlined />}
                    placeholder="6位验证码"
                    size="large"
                    maxLength={6}
                    style={{ flex: 1 }}
                  />
                  <Button
                    style={{ width: 120 }}
                    size="large"
                    loading={sendingCode}
                    disabled={countdown > 0}
                    onClick={handleSendCode}
                  >
                    {countdown > 0 ? `${countdown}s` : '获取验证码'}
                  </Button>
                </div>
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  block 
                  size="large"
                  loading={loading}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
          
          <TabPane tab={
            <span>
              <LockOutlined />
              密码登录
            </span>
          } key="password">
            <Form
              form={passwordForm}
              onFinish={handlePasswordLogin}
              layout="vertical"
            >
              <Form.Item
                name="phone"
                rules={[{ required: true, message: '请输入手机号' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="手机号" 
                  size="large"
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[{ required: true, message: '请输入密码' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="密码"
                  size="large"
                />
              </Form.Item>
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  block 
                  size="large"
                  loading={loading}
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
        
        <div style={{ 
          textAlign: 'center', 
          marginTop: 16, 
          color: '#666', 
          fontSize: '12px' 
        }}>
          推荐使用短信验证码登录，更安全便捷
        </div>
      </Card>
    </div>
  );
};

export default ResponsibleLogin; 